import {
  AREA_KIND_MAP,
  AFFECTED_AREA_OPTIONS,
  BIOMECHANICAL_CONFLICTS,
  EQUIPMENT_PRESETS,
  PAIN_SEVERITY_V2_OPTIONS,
  TRAINING_RULE_V2_OPTIONS,
  getAncestorAreas,
  getComparableArea,
} from "./settingsOptions";
import { isCompletePainIssue } from "./settingsMappers";
import { isValidPreferredTrainingDays } from "../onboarding/trainingDayDefaults";

const MAX_PAIN_DESCRIPTION = 500;
const MAX_PAIN_ISSUES = 5;
const VALID_AFFECTED_AREAS = new Set(AFFECTED_AREA_OPTIONS.map((option) => option.value));
const VALID_PAIN_SEVERITIES = new Set(PAIN_SEVERITY_V2_OPTIONS.map((option) => option.value));
const VALID_TRAINING_RULES = new Set(TRAINING_RULE_V2_OPTIONS.map((option) => option.value));
const VALID_ANALYSIS_STATUSES = new Set([
  "draft",
  "needs_clarification",
  "analyzed",
  "needs_reanalysis",
]);
const VALID_SIGNAL_TYPES = new Set(["movementPattern", "jointStressTag"]);
const VALID_SIGNAL_DECISIONS = new Set(["monitor", "caution", "blocked"]);
const VALID_CAUTION_LEVELS = new Set(["none", "low", "medium", "high"]);

function pushFieldError(fieldErrors, formErrors, path, message) {
  if (path) {
    if (!fieldErrors[path]) {
      fieldErrors[path] = message;
    }
    return;
  }

  formErrors.push(message);
}

function normalizeInteger(value) {
  if (value == null || value === "") {
    return null;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : null;
}

function normalizeString(value) {
  if (value == null) {
    return "";
  }

  return String(value).trim();
}

function toArray(value) {
  return Array.isArray(value) ? value : value == null ? [] : [value];
}

function normalizeSignalType(value) {
  const normalized = String(value || "").trim();
  return normalized || "";
}

function validateSignals({ signals, fieldErrors, formErrors, path, requireDecision }) {
  const decisionsByKey = new Map();

  toArray(signals).forEach((signal, index) => {
    if (!signal || typeof signal !== "object" || Array.isArray(signal)) {
      pushFieldError(fieldErrors, formErrors, `${path}[${index}]`, "Signal must be an object.");
      return;
    }

    const type = normalizeSignalType(signal.type);
    const value = normalizeString(signal.value).toLowerCase();
    const decision = normalizeString(signal.decision).toLowerCase();
    const cautionLevel = normalizeString(signal.cautionLevel).toLowerCase();

    if (type && !VALID_SIGNAL_TYPES.has(type)) {
      pushFieldError(fieldErrors, formErrors, `${path}[${index}].type`, "Signal type is invalid.");
    }

    if (requireDecision) {
      if (decision && !VALID_SIGNAL_DECISIONS.has(decision)) {
        pushFieldError(
          fieldErrors,
          formErrors,
          `${path}[${index}].decision`,
          "Signal decision is invalid."
        );
      }

      if (decision && VALID_SIGNAL_DECISIONS.has(decision)) {
        const effectiveCautionLevel =
          cautionLevel || (decision === "caution" ? "medium" : "none");

        if (!VALID_CAUTION_LEVELS.has(effectiveCautionLevel)) {
          pushFieldError(
            fieldErrors,
            formErrors,
            `${path}[${index}].cautionLevel`,
            "Caution level is invalid."
          );
        }

        if (decision === "caution" && effectiveCautionLevel === "none") {
          pushFieldError(
            fieldErrors,
            formErrors,
            `${path}[${index}].cautionLevel`,
            "Caution level is required for caution."
          );
        }

        if (decision !== "caution" && effectiveCautionLevel !== "none") {
          pushFieldError(
            fieldErrors,
            formErrors,
            `${path}[${index}].cautionLevel`,
            "Caution level must be none unless the signal is caution."
          );
        }
      }

      if (type && value && VALID_SIGNAL_TYPES.has(type) && VALID_SIGNAL_DECISIONS.has(decision)) {
        const key = `${type}:${value}`;
        const existingDecision = decisionsByKey.get(key);

        if (!existingDecision) {
          decisionsByKey.set(key, decision);
        }
      }
    }
  });
}

function validateMovementConstraints(trainingProfileDraft, fieldErrors, formErrors) {
  const painIssues = Array.isArray(trainingProfileDraft?.movementConstraints?.painIssues)
    ? trainingProfileDraft.movementConstraints.painIssues
    : [];
  const persistableIssues = painIssues.filter(isCompletePainIssue);

  if (persistableIssues.length > MAX_PAIN_ISSUES) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "movementConstraints.painIssues",
      `You can add up to ${MAX_PAIN_ISSUES} pain issues.`
    );
  }

  painIssues.forEach((issue, index) => {
    const issuePath = `movementConstraints.painIssues[${index}]`;
    const description = normalizeString(issue?.description);
    const affectedArea = normalizeString(issue?.affectedArea).toLowerCase();
    const painSeverity = normalizeString(issue?.painSeverity).toLowerCase();
    const trainingRule = normalizeString(issue?.trainingRule).toLowerCase();
    const analysisStatus = normalizeString(issue?.analysisStatus).toLowerCase();

    if (description.length > MAX_PAIN_DESCRIPTION) {
      pushFieldError(
        fieldErrors,
        formErrors,
        `${issuePath}.description`,
        `Pain issue description must be at most ${MAX_PAIN_DESCRIPTION} characters.`
      );
    }

    if (affectedArea && !VALID_AFFECTED_AREAS.has(affectedArea)) {
      pushFieldError(
        fieldErrors,
        formErrors,
        `${issuePath}.affectedArea`,
        "Affected area is invalid."
      );
    }

    if (painSeverity && !VALID_PAIN_SEVERITIES.has(painSeverity)) {
      pushFieldError(
        fieldErrors,
        formErrors,
        `${issuePath}.painSeverity`,
        "Pain severity is invalid."
      );
    }

    if (trainingRule && !VALID_TRAINING_RULES.has(trainingRule)) {
      pushFieldError(
        fieldErrors,
        formErrors,
        `${issuePath}.trainingRule`,
        "Training rule is invalid."
      );
    }

    if (analysisStatus && !VALID_ANALYSIS_STATUSES.has(analysisStatus)) {
      pushFieldError(
        fieldErrors,
        formErrors,
        `${issuePath}.analysisStatus`,
        "Analysis status is invalid."
      );
    }

    validateSignals({
      signals: issue?.detectedSignals,
      fieldErrors,
      formErrors,
      path: `${issuePath}.detectedSignals`,
      requireDecision: false,
    });
    validateSignals({
      signals: issue?.confirmedSignals,
      fieldErrors,
      formErrors,
      path: `${issuePath}.confirmedSignals`,
      requireDecision: true,
    });
  });
}

function hasBiomechanicalConflict(left, right) {
  const comparableLeft = getComparableArea(left);
  const comparableRight = getComparableArea(right);

  if (!comparableLeft || !comparableRight) {
    return false;
  }

  return (BIOMECHANICAL_CONFLICTS[comparableLeft] || []).includes(comparableRight);
}

export function validateTrainingProfileDraft(
  trainingProfileDraft,
  availabilityOptions = {}
) {
  const fieldErrors = {};
  const formErrors = [];
  const primaryGoal = normalizeString(trainingProfileDraft?.primaryGoal);
  const experience = normalizeString(trainingProfileDraft?.experience);
  const sessionsPerWeek = normalizeInteger(
    trainingProfileDraft?.availability?.sessionsPerWeek
  );
  const durationPerSession = normalizeInteger(
    trainingProfileDraft?.availability?.durationPerSession
  );
  const preferredTrainingDays = trainingProfileDraft?.availability?.preferredTrainingDays;
  const equipmentPreset = normalizeString(
    trainingProfileDraft?.environment?.equipmentPreset
  );
  const availableEquipment = Array.isArray(
    trainingProfileDraft?.environment?.availableEquipment
  )
    ? trainingProfileDraft.environment.availableEquipment.filter(Boolean)
    : [];
  const primaryFocus = normalizeString(
    trainingProfileDraft?.musclePriorities?.primaryFocus
  );
  const secondaryFocuses = Array.isArray(
    trainingProfileDraft?.musclePriorities?.secondaryFocuses
  )
    ? trainingProfileDraft.musclePriorities.secondaryFocuses.filter(Boolean)
    : [];
  const deprioritizedArea = normalizeString(
    trainingProfileDraft?.musclePriorities?.deprioritizedArea
  );
  const sessionsPerWeekValues = Array.isArray(
    availabilityOptions?.sessionsPerWeek
  )
    ? availabilityOptions.sessionsPerWeek.map(Number)
    : [];
  const durationPerSessionValues = Array.isArray(
    availabilityOptions?.durationPerSession
  )
    ? availabilityOptions.durationPerSession.map(Number)
    : [];

  if (!primaryGoal) {
    pushFieldError(fieldErrors, formErrors, "primaryGoal", "Primary goal is required.");
  }

  if (!experience) {
    pushFieldError(fieldErrors, formErrors, "experience", "Experience is required.");
  }

  if (
    sessionsPerWeek == null ||
    !sessionsPerWeekValues.includes(sessionsPerWeek)
  ) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "availability.sessionsPerWeek",
      "Select an available sessions-per-week value."
    );
  }

  if (
    durationPerSession == null ||
    !durationPerSessionValues.includes(durationPerSession)
  ) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "availability.durationPerSession",
      "Select an available session duration."
    );
  }

  if (
    preferredTrainingDays != null &&
    sessionsPerWeek != null &&
    !isValidPreferredTrainingDays(preferredTrainingDays, sessionsPerWeek)
  ) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "availability.preferredTrainingDays",
      `Select exactly ${sessionsPerWeek} unique training days.`
    );
  }

  if (equipmentPreset && !Object.prototype.hasOwnProperty.call(EQUIPMENT_PRESETS, equipmentPreset)) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "environment.equipmentPreset",
      "Equipment preset is invalid."
    );
  }

  if (availableEquipment.length < 1) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "environment.availableEquipment",
      "Available equipment must contain at least one item."
    );
  }

  if (secondaryFocuses.length > 2) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "musclePriorities.secondaryFocuses",
      "Secondary focuses must contain at most two values."
    );
  }

  if (
    primaryFocus &&
    secondaryFocuses.some((secondaryFocus) => secondaryFocus === primaryFocus)
  ) {
    pushFieldError(
      fieldErrors,
      formErrors,
      "musclePriorities.secondaryFocuses",
      "Secondary focuses cannot repeat the primary focus."
    );
  }

  if (deprioritizedArea) {
    if (AREA_KIND_MAP[deprioritizedArea] === "micro") {
      pushFieldError(
        fieldErrors,
        formErrors,
        "musclePriorities.deprioritizedArea",
        "Deprioritized area cannot target a micro-focus."
      );
    }

    if (
      deprioritizedArea === primaryFocus ||
      secondaryFocuses.includes(deprioritizedArea)
    ) {
      pushFieldError(
        fieldErrors,
        formErrors,
        "musclePriorities.deprioritizedArea",
        "Deprioritized area cannot also be a focus area."
      );
    }

    [primaryFocus, ...secondaryFocuses].filter(Boolean).forEach((focusArea) => {
      if (getAncestorAreas(focusArea).includes(deprioritizedArea)) {
        pushFieldError(
          fieldErrors,
          formErrors,
          "musclePriorities.deprioritizedArea",
          "You cannot deprioritize a parent area of a selected focus."
        );
      }

      if (hasBiomechanicalConflict(focusArea, deprioritizedArea)) {
        pushFieldError(
          fieldErrors,
          formErrors,
          "musclePriorities.deprioritizedArea",
          "This focus and deprioritized combination is blocked by the current rules."
        );
      }
    });
  }

  validateMovementConstraints(trainingProfileDraft, fieldErrors, formErrors);

  return {
    ok: Object.keys(fieldErrors).length === 0 && formErrors.length === 0,
    fieldErrors,
    formErrors,
  };
}
